import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { directory, isForbidden, isNotFound, services } from '@cwos/api-client';
import type { services as SVC } from '@cwos/api-client';
import {
  DegradedSourceNote,
  Origin,
  Pill,
  Refused,
  ScopeNote,
  SkeletonSet,
  SuccessEmpty,
} from '@cwos/ui-admin';
import { mayListWorkers, mayReadIntegrationsHealth, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { listPhase, nameResolver } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { SettingsField, SettingsHeader } from '../settings/common.js';
import { formatClock, formatDay } from '../time/timeLogic.js';
import { OWN_CASELOAD } from '../delivery.js';
import {
  degradedConnectorNames,
  scheduledServiceProvenance,
  serviceEventProvenance,
  useConnectorLookup,
} from './provenance.js';
import {
  ATTENDANCE_LABELS,
  clientNameResolver,
  deliveredVisitDraftFromPlan,
  deliveredVisitPayload,
  deliveriesInWindow,
  emptyDeliveredVisitDraft,
  emptyPlannedVisitDraft,
  plannedVisitCreatePayload,
  plannedVisitDraftFrom,
  plannedVisitUpdatePayload,
  validateDeliveredVisit,
  validatePlannedVisit,
  visitErrorMessage,
  visitStatusLabel,
  visitsInWindow,
  type DeliveredVisitDraft,
  type PlannedVisitDraft,
} from './visitLogic.js';
import { LoadFailure } from '../LoadFailure.js';
import { calendarStateLabel, visitState } from '../schedule/scheduleBoard.js';

/**
 * Visits — what was planned for a client, and what was actually delivered.
 *
 * THE SCREEN EXISTS BECAUSE THE FIRST TWO LINKS OF THE OPERATING LOOP HAD NO
 * CALLER. `POST /services/scheduled` has been routed, guarded and tested since
 * 1757040000000, and `services.createScheduled` / `updateScheduled` /
 * `cancelScheduled` have wrapped it in `@cwos/api-client`; `POST
 * /services/events` reaches `services/repository.ts:691`, the only production
 * writer of `service_event` besides a Ritten connector that has never been run
 * against a real tenant. Neither had a caller anywhere in `apps/admin/src` or
 * `apps/worker-mobile/src`. So a pilot organization could not plan a visit and
 * could not record one, which makes the rest unreachable BY CONSTRUCTION rather
 * than merely unused: `delivery.ts` subtracts the delivered set from the
 * planned one to find missed visits and a subtraction from an empty set finds
 * none, and a `service_documentation_status` row exists per service event, so
 * the documentation queue was permanently empty and the worker app's wrap-up
 * flow had nothing to document.
 *
 * VISITS IS THE PLANNING AND RECORDING LIST; SCHEDULE IS THE READ CALENDAR.
 * `/scheduling` now draws these same planned visits beside the shifts (owner
 * P0: Schedule is the organization's calendar), read-only, and its visit drawer
 * opens a visit HERE (`/visits?occurrence=<id>`) — because this screen holds the
 * actions: plan, edit, call off, record what happened. The keys still differ
 * and still decide: a client VISIT is `service:read` (and, on the delivered
 * side, `service:write` / `service:write_all`), which a `schedule:read` holder
 * need not have — Schedule shows visits only to a reader who holds it.
 *
 * WHY BOTH LISTS ARE ON ONE SCREEN. Recording a delivery against the plan it
 * fulfilled is the only way `scheduledServiceId` ever gets set, and that column
 * is what `deriveDeliveryToday` joins on. A record entered from a blank form
 * leaves its plan in the set difference — reported as a missed visit, with an
 * evidence link, that did not happen. Putting the recording action ON the
 * planned row is what makes the link the default rather than the exception.
 */

const DAY_MS = 86_400_000;

/**
 * "Delivered · Started (empty)" — the calendar's state, capitalised for a pill,
 * in the calendar's words ("No longer in Ritten" when the connector is named).
 */
function canonicalLabel(row: SVC.ScheduledService, connectorName: string | null): string {
  const { state, detail } = visitState(row);
  const label = calendarStateLabel(state, connectorName);
  return `${label[0]!.toUpperCase()}${label.slice(1)}${detail ? ` · ${detail}` : ''}`;
}

export function Visits() {
  const qc = useQueryClient();
  const permissions = usePermissions();
  const { has } = permissions;
  const [days, setDays] = useState(7);
  /**
   * `?occurrence=<id>` — the visit somebody opened from the calendar or a
   * worker's profile. Read once, at mount, from the router when there is one
   * (the screen also renders bare, in component doubles); closing it is local.
   */
  const router = useRouter({ warn: false });
  const [focusId, setFocusId] = useState<string | null>(() => {
    const id = (router?.state.location.search as { occurrence?: unknown } | undefined)?.occurrence;
    return typeof id === 'string' && id !== '' ? id : null;
  });

  /**
   * WHICH KEY OPENS WHICH DOOR, read off the routes rather than assumed.
   *
   * `can()` and never `has()`: every one of these is `guard(request, reply, …)`
   * or a bare `can(principal.authz, …)`, all at organization scope, so a
   * PROGRAM-scoped grant is a 403 and not a narrower answer. The flattened
   * "holds it somewhere" question would read that grant as organization-wide
   * (61275c8) and put a control here the server refuses.
   *
   *   plan / edit / cancel  `schedule:write` — the ROSTER key. 1757040000000
   *     settled that booking a client visit is roster authority and that
   *     whoever builds the plan must be able to staff people who do not report
   *     to them.
   *   record a delivery     `service:write` OR `service:write_all` —
   *     `guardServiceEventSurface` admits either.
   *   choose WHO delivered  `service:write_all` alone, org_admin by seed
   *     (1757010000000). A plain `service:write` holder does not get a narrower
   *     choice; `resolveDeliverySubject` replaces the subject with their own
   *     worker row, and refuses outright if they have none. So the worker picker
   *     is not merely hidden from them — sending a `workerId` they inherited
   *     from a prefilled roster row would turn a legitimate recording into a
   *     403. See `deliveredVisitPayload`.
   */
  const canPlan = permissions.can('schedule:write');
  const mayAttributeToOthers = permissions.can('service:write_all');
  const canRecord = permissions.can('service:write') || mayAttributeToOthers;
  const canListWorkers = mayListWorkers(permissions);
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canListWorkers;
  /**
   * PH-UX-002. The optional cross-check `dataHealth.ts` gates the same way
   * (`mayReadIntegrationsHealth`): a reader who cannot hold `integrations:read`
   * never fires this request, and gets an empty lookup back — every row then
   * renders exactly as it would if nothing were known about its connector,
   * which is the true state of a set nobody could read.
   */
  const connectorLookup = useConnectorLookup(mayReadIntegrationsHealth(has));
  /**
   * WHOSE ATTESTATION THIS BECOMES — said only from a set somebody could read.
   *
   * `mayAttributeToOthers` is `can()` and therefore default-deny, which is the
   * right answer for `deliveredVisitPayload`, `validateDeliveredVisit` and the
   * "Delivered by" picker: an unresolved answer must narrow the write and hide
   * the control, never widen either. It is the WRONG answer for the sentence
   * "Recording on somebody else's behalf needs service:write_all", which is a
   * statement about the reader's account — and a `service:write` holder whose
   * `/auth/me` refresh failed can reach this form (`canRecord` is satisfied by
   * their cached `service:write`) and be told they lack a key nobody could
   * check.
   *
   * Written as `rosterWithheld` is written eight lines up, and for the same
   * reason: the house form for "claimed only from a RESOLVED set".
   *
   * `authorityGate` is deliberately NOT used here. It answers with `has()` —
   * "held somewhere" — and this door is `can()` at ORGANIZATION scope, because
   * `resolveDeliverySubject` replaces the subject outright rather than
   * narrowing it. A program-scoped `service:write_all` is a 403 and not a
   * smaller choice, so `has()` would call this reader allowed and put a picker
   * in front of them that cannot work.
   */
  const attributionWithheld =
    !permissions.isLoading && !permissions.isError && !mayAttributeToOthers;
  const attributionUnknown = permissions.isError && !mayAttributeToOthers;

  const [planning, setPlanning] = useState(false);
  const [planDraft, setPlanDraft] = useState<PlannedVisitDraft>(() =>
    emptyPlannedVisitDraft(new Date()),
  );
  const [planError, setPlanError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<PlannedVisitDraft>(() =>
    emptyPlannedVisitDraft(new Date()),
  );
  const [editError, setEditError] = useState<string | null>(null);
  /**
   * WHICH visit's call-off failed, and what the server said.
   *
   * Keyed by id rather than held as a bare string, for the reason
   * `Scheduling.tsx` keys its own: this list shows every planned visit in the
   * window, and a message that floats at the bottom of the card names none of
   * them. An operator who called off Tuesday's visit and read "we couldn't call
   * that visit off" under a list of six rows cannot tell which row it is about.
   */
  const [cancelError, setCancelError] = useState<{ id: string; message: string } | null>(null);

  const [recording, setRecording] = useState(false);
  const [recordDraft, setRecordDraft] = useState<DeliveredVisitDraft>(() =>
    emptyDeliveredVisitDraft(new Date()),
  );
  const [recordError, setRecordError] = useState<string | null>(null);

  const window = useMemo(() => {
    const now = new Date();
    return {
      from: new Date(now.getTime() - days * DAY_MS),
      to: new Date(now.getTime() + days * DAY_MS),
    };
  }, [days]);

  /**
   * The window the screen already filtered to, asked for as the window.
   *
   * `visitsInWindow` / `deliveriesInWindow` below kept `t >= from && t <= to`
   * out of a response that carried the tenant's entire visit history, so the
   * ± 30 day view downloaded six months (or three years) to render sixty days.
   * The bounds are the SAME two instants and the server comparison is closed at
   * both ends, so the two client filters keep exactly the rows they kept
   * before — they are left in place as the definition this window is derived
   * from, not as a second, weaker one.
   *
   * The window is part of the query key. Changing the ± selector changes the
   * request, so a cache entry can never answer a wider question than it was
   * fetched for.
   */
  const windowKey = useMemo(
    () => ({ from: window.from.toISOString(), to: window.to.toISOString() }),
    [window],
  );
  const roster = useQuery({
    queryKey: ['services', 'scheduled', windowKey.from, windowKey.to],
    queryFn: () => services.scheduled(windowKey),
    enabled: has('service:read'),
  });
  /** The opened visit, through the list's own scope (404 = missing OR out of reach). */
  const focused = useQuery({
    queryKey: ['services', 'scheduled', 'one', focusId],
    queryFn: () => services.scheduledById(focusId!),
    enabled: focusId !== null && has('service:read'),
  });
  const delivered = useQuery({
    queryKey: ['services', 'events', windowKey.from, windowKey.to],
    queryFn: () => services.events(windowKey),
    enabled: has('service:read'),
  });
  const clients = useQuery({
    queryKey: ['directory', 'clients'],
    queryFn: () => directory.clients(),
    enabled: has('directory:read'),
  });
  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });
  const serviceTypes = useQuery({
    queryKey: ['services', 'types'],
    queryFn: () => services.types(),
    enabled: has('service:read'),
  });

  // Both directories are SECONDARY reads with no error branch of their own. A
  // single 500 on either used to relabel every row on a screen as somebody the
  // reader was not allowed to see; `nameResolver` keeps "failed", "not yet" and
  // "outside your scope" as three different sentences.
  const nameOf = useMemo(() => nameResolver(workers, canListWorkers), [workers, canListWorkers]);

  /**
   * The same three-way distinction for the CLIENT directory, and the kind
   * rather than the object: `authorityGate` builds a fresh record on every
   * render, so depending on it directly would rebuild `clientLabel` every time
   * and quietly turn its `useMemo` into a no-op.
   */
  const directoryGate = authorityGate(permissions, 'directory:read').kind;

  /**
   * A client, by the only name this surface ever learns.
   *
   * INITIALS, and there is no fuller name to reach for: `GET /directory/clients`
   * serves `ClientListView`, which is built field by field and never carries a
   * full name or a `dob`. A full name is one audited reveal on one client
   * (`client:reveal_name`) and is deliberately not something a roster screen
   * assembles for itself.
   */
  const clientLabel = useMemo(
    () => clientNameResolver(directoryGate, clients),
    // `clients.data` / `clients.isError` and not `clients`: react-query hands
    // back a fresh wrapper every render, so depending on the object would
    // rebuild this map on every keystroke on the screen.
    [clients.data, clients.isError, directoryGate],
  );

  const typeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of serviceTypes.data ?? []) map.set(t.id, t.name);
    return (id: string | null): string | null => (id === null ? null : (map.get(id) ?? null));
  }, [serviceTypes.data]);

  function invalidateVisits(): void {
    void qc.invalidateQueries({ queryKey: ['services'] });
  }

  const planVisit = useMutation({
    mutationFn: () => services.createScheduled(plannedVisitCreatePayload(planDraft)),
    onSuccess: () => {
      setPlanning(false);
      setPlanDraft(emptyPlannedVisitDraft(new Date()));
      setPlanError(null);
      invalidateVisits();
    },
    onError: (err: unknown) => {
      setPlanError(visitErrorMessage(err, 'We couldn’t plan that visit.'));
    },
  });

  const editVisit = useMutation({
    mutationFn: (id: string) => services.updateScheduled(id, plannedVisitUpdatePayload(editDraft)),
    onSuccess: () => {
      setEditingId(null);
      setEditError(null);
      invalidateVisits();
    },
    onError: (err: unknown) => {
      setEditError(visitErrorMessage(err, 'We couldn’t save those changes.'));
    },
  });

  /**
   * CANCELLING IS A STATUS PATCH, NOT A REMOVAL, and the row stays on the list
   * afterwards saying so. "Planned and then called off" and "never planned" are
   * different facts about a client's week and only one of them is a care gap.
   *
   * It has an error branch — a `cancelShift` on the Scheduling screen did not,
   * and a failed cancel there simply closed the confirm and left the row
   * looking untouched, which read as a cancel that worked. That is now fixed
   * there too, and this screen has taken the other half of the same lesson:
   * BOTH the pending state and the failure are keyed to the row.
   */
  const cancelVisit = useMutation({
    mutationFn: (id: string) => services.cancelScheduled(id),
    onMutate: () => {
      setCancelError(null);
    },
    onSuccess: () => {
      setCancelError(null);
      invalidateVisits();
    },
    onError: (err: unknown, id: string) => {
      setCancelError({ id, message: visitErrorMessage(err, 'We couldn’t call that visit off.') });
    },
  });

  const recordVisit = useMutation({
    mutationFn: () =>
      services.createEvent(deliveredVisitPayload(recordDraft, { mayAttributeToOthers })),
    onSuccess: () => {
      setRecording(false);
      setRecordDraft(emptyDeliveredVisitDraft(new Date()));
      setRecordError(null);
      invalidateVisits();
    },
    onError: (err: unknown) => {
      setRecordError(visitErrorMessage(err, 'We couldn’t record that visit.'));
    },
  });

  // Unknown is not denied. `has()` answers `false` while `/auth/me` is in flight
  // and `false` after it failed; this branch used to render "you don't have
  // access to visits" off all three, which tells a reader who holds
  // `service:read` something false about their own account. Only `refused` is a
  // resolved answer and only `refused` gets the sentence.
  const readGate = authorityGate(permissions, 'service:read');
  if (!gateOpen(readGate)) {
    return (
      <GateNotice
        gate={readGate}
        what="see planned and delivered visits"
        heading="You don’t have access to visits"
        detail="Reading planned and delivered visits needs the service permission."
        onRetry={permissions.refetch}
      />
    );
  }

  // Both reads narrow in the SQL and neither refuses on scope, so a 403 here is
  // the key itself being gone — a refusal, and a retry button cannot change it.
  if (roster.isError && isForbidden(roster.error)) {
    return (
      <Refused
        heading="Visits are not shown to you"
        message="Reading planned and delivered visits needs the service permission. This is not an empty week."
      />
    );
  }
  if (roster.isLoading || delivered.isLoading) return <SkeletonSet surface="table" rows={6} />;
  if (roster.isError && !roster.data) {
    return (
      <LoadFailure
        error={roster.error}
        message="We couldn’t load the planned visits just now. Do not read this as an empty window."
        onRetry={() => void roster.refetch()}
      />
    );
  }

  const planned = visitsInWindow(roster.data?.scheduledServices ?? [], window.from, window.to);
  const deliveries = deliveriesInWindow(
    delivered.data?.serviceEvents ?? [],
    window.from,
    window.to,
  );

  /**
   * WHOSE ROWS CAME BACK, taken from the response and never re-derived from a
   * permission list. Both reads narrow in the SQL — an out-of-scope row is
   * MISSING rather than refused — and each response carries its own `scoped`
   * flag because the two are widened by DIFFERENT keys: `schedule:manage` for
   * the plan, `service:read_all` for the deliveries. A caller can hold one frame
   * and not the other, so neither may be read as the other.
   */
  const plannedScoped = roster.data?.scoped ?? false;
  const deliveredScoped = delivered.data?.scoped ?? false;

  const clientPicker = pickerState(
    listPhase(clients, clients.data?.length),
    'clients',
    'Choose a client…',
  );
  const typePicker = pickerState(
    listPhase(serviceTypes, serviceTypes.data?.length),
    'service types',
    'No service type',
  );
  const workerPicker = rosterWithheld
    ? pickerState({ kind: 'refused' }, 'people', 'Choose a worker…')
    : canListWorkers
      ? pickerState(listPhase(workers, workers.data?.length), 'people', 'Choose a worker…')
      : // Not refused — NOT YET KNOWN. The query is disabled until the
        // permission set resolves, and saying "refused" from an unresolved set
        // accuses the reader of lacking access nobody has checked.
        pickerState({ kind: 'loading' }, 'people', 'Choose a worker…');

  const planErrors = validatePlannedVisit(planDraft);
  const canSubmitPlan = Object.keys(planErrors).length === 0;
  const editErrors = validatePlannedVisit(editDraft);
  const canSubmitEdit = Object.keys(editErrors).length === 0;
  const recordErrors = validateDeliveredVisit(recordDraft, { mayAttributeToOthers });
  const canSubmitRecord = Object.keys(recordErrors).length === 0;

  function startEdit(row: SVC.ScheduledService): void {
    setEditingId(row.id);
    setEditDraft(plannedVisitDraftFrom(row));
    setEditError(null);
    setPlanning(false);
  }

  function startRecordFromPlan(row: SVC.ScheduledService): void {
    setRecordDraft(deliveredVisitDraftFromPlan(row, new Date()));
    setRecording(true);
    setRecordError(null);
  }

  function visitForm(
    draft: PlannedVisitDraft,
    setDraft: (next: PlannedVisitDraft) => void,
    errors: Record<string, string>,
  ) {
    return (
      <div className="org-create-grid">
        <SettingsField label="Client" error={errors['clientId']}>
          <select
            value={draft.clientId}
            disabled={clientPicker.disabled}
            onChange={(e) => setDraft({ ...draft, clientId: e.target.value })}
          >
            <option value="">{clientPicker.placeholder}</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.initials}
              </option>
            ))}
          </select>
        </SettingsField>
        <SettingsField
          label="Worker"
          hint="A plan may name a time before it names a worker — an unstaffed visit is a real row on the roster."
        >
          <select
            value={draft.workerId}
            disabled={workerPicker.disabled}
            onChange={(e) => setDraft({ ...draft, workerId: e.target.value })}
          >
            <option value="">Nobody yet</option>
            {(workers.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.displayName}
              </option>
            ))}
          </select>
        </SettingsField>
        <SettingsField label="Service type">
          <select
            value={draft.serviceTypeId}
            disabled={typePicker.disabled}
            onChange={(e) => setDraft({ ...draft, serviceTypeId: e.target.value })}
          >
            <option value="">{typePicker.placeholder}</option>
            {(serviceTypes.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </SettingsField>
        <SettingsField label="Starts" error={errors['startsAt']}>
          <input
            type="datetime-local"
            value={draft.startsAt}
            onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
          />
        </SettingsField>
        <SettingsField label="Ends" error={errors['endsAt']}>
          <input
            type="datetime-local"
            value={draft.endsAt}
            onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
          />
        </SettingsField>
        <SettingsField label="Note" error={errors['note']}>
          <input
            value={draft.note}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            placeholder="What this visit is for"
          />
        </SettingsField>
      </div>
    );
  }

  /** One planned visit, with its actions — the list's row and the opened visit's. */
  function plannedRow(row: SVC.ScheduledService) {
    const type = typeLabel(row.serviceTypeId);
    const provenance = scheduledServiceProvenance(row, connectorLookup);
    const lastSynced =
      provenance.kind === 'imported' && provenance.lastSyncedAt
        ? `${formatDay(provenance.lastSyncedAt)}, ${formatClock(provenance.lastSyncedAt)}`
        : null;
    return (
      <li key={row.id} className="tc-row">
        <div style={{ flex: 1 }}>
          <span className="cmp-control-title">{clientLabel(row.clientId)}</span>
          <p className="rn-muted">
            {formatDay(row.startsAt)} · {formatClock(row.startsAt)}
            {row.endsAt ? ` – ${formatClock(row.endsAt)}` : ''}
            {' · '}
            {/* Every credited staff party — a group visit has no single worker. */}
            {(row.workerIds ?? (row.workerId ? [row.workerId] : [])).map(nameOf).join(', ') ||
              'Nobody staffed'}
            {type ? ` · ${type}` : ''}
            {/* Quiet by design when the record is merely imported —
                the ordinary case must read as metadata, not an
                alarm, or a supervisor scanning a mostly-imported
                list learns to skip it. The vendor-absent case below
                is the deliberate exception: `Origin` breaks out of
                this line for exactly that fact. */}
            {provenance.kind === 'imported' && !provenance.vendorAbsentSince ? (
              <>
                {' · '}
                <Origin
                  source={row.source}
                  connectorName={provenance.connectorName}
                  lastSyncedAt={lastSynced}
                />
              </>
            ) : null}
          </p>
          {provenance.kind === 'imported' && provenance.vendorAbsentSince ? (
            <p className="rn-muted">
              <Origin
                source={row.source}
                connectorName={provenance.connectorName}
                vendorAbsentSince={provenance.vendorAbsentSince}
                lastSyncedAt={lastSynced}
              />
            </p>
          ) : null}
        </div>
        <Pill variant={row.status === 'cancelled' ? 'unsure' : 'plain'} dot>
          {visitStatusLabel(row.status)}
        </Pill>
        {row.deliveredBucket ? (
          // What the EHR says happened — beside Phibian's own status, never in
          // place of it — in the CANONICAL bucket's words, the ones the calendar
          // and every delivered metric count: a blank past row its encounter
          // proves reads delivered, not "Not started".
          <Pill variant="plain">
            {canonicalLabel(row, provenance.kind === 'imported' ? provenance.connectorName : null)}
          </Pill>
        ) : row.occurrenceState ? (
          <Pill variant="plain">{services.OCCURRENCE_STATE_LABELS[row.occurrenceState]}</Pill>
        ) : null}
        {canPlan && row.status !== 'cancelled' ? (
          <>
            <button type="button" className="rn-btn-quiet" onClick={() => startEdit(row)}>
              Edit
            </button>
            {/* PENDING IS KEYED TO THIS ROW, and so is the failure.
                `cancelVisit.isPending` alone describes the mutation,
                not the visit: one click disabled "Call it off" on
                every planned visit in the window and said which one
                was in flight on none of them, so an operator watched
                the whole card freeze with no way to tell what they
                had actually asked for. And there was no pending label
                at all — the button greyed out and kept its resting
                word, which is indistinguishable from a screen that
                has stopped responding. */}
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={cancelVisit.isPending && cancelVisit.variables === row.id}
              onClick={() => cancelVisit.mutate(row.id)}
            >
              {cancelVisit.isPending && cancelVisit.variables === row.id
                ? 'Calling off…'
                : cancelError?.id === row.id
                  ? 'Try again'
                  : 'Call it off'}
            </button>
          </>
        ) : null}
        {cancelError?.id === row.id ? (
          <p
            className="tc-warn"
            role="alert"
            style={{ flexBasis: '100%', marginTop: 'var(--space-6)' }}
          >
            {cancelError.message} This visit is still planned.
          </p>
        ) : null}
        {canRecord && row.status !== 'cancelled' ? (
          <button type="button" className="rn-btn" onClick={() => startRecordFromPlan(row)}>
            Record what happened
          </button>
        ) : null}
        {editingId === row.id ? (
          <div className="org-create" style={{ flexBasis: '100%' }}>
            {visitForm(editDraft, setEditDraft, editErrors)}
            <div className="tc-drawer-actions">
              <button
                type="button"
                className="rn-btn"
                disabled={!canSubmitEdit || editVisit.isPending}
                onClick={() => editVisit.mutate(row.id)}
              >
                {editVisit.isPending ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={() => setEditingId(null)}>
                Cancel
              </button>
            </div>
            {editError ? (
              <p className="tc-warn" role="alert">
                {editError}
              </p>
            ) : null}
          </div>
        ) : null}
      </li>
    );
  }

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Visits"
        blurb="What was planned for a client, and what was actually delivered."
      />

      {focusId ? (
        <section className="rn-card" aria-label="The visit you opened">
          <div className="rn-card-head">
            <h2 className="rn-card-title">The visit you opened</h2>
            <button type="button" className="rn-btn-quiet" onClick={() => setFocusId(null)}>
              Show the whole window
            </button>
          </div>
          {focused.isLoading ? (
            <SkeletonSet surface="table" rows={1} />
          ) : focused.isError && isNotFound(focused.error) ? (
            // 404 is "missing" OR "outside your reach", indistinguishable on purpose.
            <p className="rn-muted">
              That visit is not one you can open here — it is outside what you can see, or it no
              longer exists.
            </p>
          ) : focused.isError ? (
            <LoadFailure
              error={focused.error}
              message="We couldn’t load that visit just now."
              onRetry={() => void focused.refetch()}
            />
          ) : focused.data ? (
            <ul className="tc-rows">{plannedRow(focused.data)}</ul>
          ) : null}
        </section>
      ) : null}

      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Planned visits</h2>
          <div style={{ display: 'flex', gap: 'var(--space-12)' }}>
            <label className="cmp-filter">
              <span className="rn-muted">Window</span>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                aria-label="Visit window"
              >
                <option value={7}>± 7 days</option>
                <option value={14}>± 14 days</option>
                <option value={30}>± 30 days</option>
              </select>
            </label>
            {canPlan ? (
              <button
                type="button"
                className="rn-btn"
                onClick={() => {
                  setPlanning((v) => !v);
                  setPlanError(null);
                }}
              >
                {planning ? 'Not now' : 'Plan a visit'}
              </button>
            ) : null}
          </div>
        </div>

        {plannedScoped ? <ScopeNote scope={OWN_CASELOAD} /> : null}

        {planning ? (
          <div className="org-create">
            {visitForm(planDraft, setPlanDraft, planErrors)}
            <div className="tc-drawer-actions">
              <button
                type="button"
                className="rn-btn"
                disabled={!canSubmitPlan || planVisit.isPending}
                onClick={() => planVisit.mutate()}
              >
                {planVisit.isPending ? 'Planning…' : 'Plan it'}
              </button>
            </div>
            {planError ? (
              <p className="tc-warn" role="alert">
                {planError}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* PH-UX-002: one note per connector actually feeding a planned row on
            screen, never a screen-wide "something somewhere is degraded"
            claim — see provenance.ts's `degradedConnectorNames`. */}
        <DegradedSourceNote connectorNames={degradedConnectorNames(planned, connectorLookup)} />

        {planned.length === 0 ? (
          <SuccessEmpty
            heading="No visits planned in this window"
            message="A planned visit appears here once somebody books one."
          />
        ) : (
          <ul className="tc-rows">{planned.filter((row) => row.id !== focusId).map(plannedRow)}</ul>
        )}
      </section>

      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Delivered visits</h2>
          {canRecord ? (
            <button
              type="button"
              className="rn-btn"
              onClick={() => {
                if (!recording) setRecordDraft(emptyDeliveredVisitDraft(new Date()));
                setRecording((v) => !v);
                setRecordError(null);
              }}
            >
              {recording ? 'Not now' : 'Record a visit'}
            </button>
          ) : null}
        </div>

        {deliveredScoped ? <ScopeNote scope={OWN_CASELOAD} /> : null}

        {recording ? (
          <div className="org-create">
            {/* Whether this record is linked to a plan is not something a reader
                should have to infer. The link is the column `delivery.ts` joins
                on, and a record made without one leaves its plan in the missed-
                visit set — an alarm about care that did happen. */}
            <p className="rn-muted">
              {recordDraft.scheduledServiceId
                ? 'This will be recorded against the planned visit you chose, so the plan is not reported as missed.'
                : 'This is not linked to a planned visit. Record it from a row above if it was planned.'}
            </p>
            {attributionWithheld ? (
              // Not a hidden field with a silent default: the server attributes
              // the delivery to the caller's own worker row and refuses any
              // other subject, so saying whose attestation this becomes is
              // describing what happens rather than reassuring about it.
              <p className="rn-muted">
                This will be recorded as delivered by you. Recording on somebody else’s behalf needs{' '}
                <code>service:write_all</code>.
              </p>
            ) : attributionUnknown ? (
              // The write is narrowed the same way — that is the safe direction
              // and it is what the payload does — but the REASON is different
              // and the reader is owed the difference. Naming a permission they
              // do not hold would be a guess dressed as an answer.
              <p className="rn-muted">
                This will be recorded as delivered by you. We couldn’t check whether you may record
                on somebody else’s behalf — that is a connection problem, not a decision about your
                account.
              </p>
            ) : null}
            <div className="org-create-grid">
              <SettingsField label="Client" error={recordErrors['clientId']}>
                <select
                  value={recordDraft.clientId}
                  disabled={clientPicker.disabled}
                  onChange={(e) => setRecordDraft({ ...recordDraft, clientId: e.target.value })}
                >
                  <option value="">{clientPicker.placeholder}</option>
                  {(clients.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.initials}
                    </option>
                  ))}
                </select>
              </SettingsField>
              {mayAttributeToOthers ? (
                <SettingsField label="Delivered by" error={recordErrors['workerId']}>
                  <select
                    value={recordDraft.workerId}
                    disabled={workerPicker.disabled}
                    onChange={(e) => setRecordDraft({ ...recordDraft, workerId: e.target.value })}
                  >
                    <option value="">{workerPicker.placeholder}</option>
                    {(workers.data ?? []).map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.displayName}
                      </option>
                    ))}
                  </select>
                </SettingsField>
              ) : null}
              <SettingsField label="Service type">
                <select
                  value={recordDraft.serviceTypeId}
                  disabled={typePicker.disabled}
                  onChange={(e) =>
                    setRecordDraft({ ...recordDraft, serviceTypeId: e.target.value })
                  }
                >
                  <option value="">{typePicker.placeholder}</option>
                  {(serviceTypes.data ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </SettingsField>
              <SettingsField label="Happened at" error={recordErrors['occurredAt']}>
                <input
                  type="datetime-local"
                  value={recordDraft.occurredAt}
                  onChange={(e) => setRecordDraft({ ...recordDraft, occurredAt: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Ended at" error={recordErrors['endedAt']}>
                <input
                  type="datetime-local"
                  value={recordDraft.endedAt}
                  onChange={(e) => setRecordDraft({ ...recordDraft, endedAt: e.target.value })}
                />
              </SettingsField>
              <SettingsField
                label="Minutes"
                hint="Leave blank if the record does not say. 0 is a real answer for a visit nobody attended."
                error={recordErrors['minutes']}
              >
                <input
                  inputMode="numeric"
                  value={recordDraft.minutes}
                  onChange={(e) => setRecordDraft({ ...recordDraft, minutes: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="What happened" error={recordErrors['attendanceStatus']}>
                <select
                  value={recordDraft.attendanceStatus}
                  onChange={(e) =>
                    setRecordDraft({
                      ...recordDraft,
                      attendanceStatus: e.target.value as DeliveredVisitDraft['attendanceStatus'],
                    })
                  }
                >
                  <option value="">Choose…</option>
                  {(Object.keys(ATTENDANCE_LABELS) as SVC.AttendanceStatus[])
                    // `unknown` is an imported-only reading, never a manual choice.
                    .filter((key) => key !== 'unknown')
                    .map((key) => (
                      <option key={key} value={key}>
                        {ATTENDANCE_LABELS[key]}
                      </option>
                    ))}
                </select>
              </SettingsField>
              <SettingsField
                label="Note"
                hint="Stored with the visit. It is never returned by this list — the narrative is read through documentation only."
                error={recordErrors['note']}
              >
                <input
                  value={recordDraft.note}
                  onChange={(e) => setRecordDraft({ ...recordDraft, note: e.target.value })}
                />
              </SettingsField>
            </div>
            <label className="cmp-filter">
              <input
                type="checkbox"
                checked={recordDraft.isBillable}
                onChange={(e) => setRecordDraft({ ...recordDraft, isBillable: e.target.checked })}
              />
              <span className="rn-muted">Billable</span>
            </label>
            <div className="tc-drawer-actions">
              <button
                type="button"
                className="rn-btn"
                disabled={!canSubmitRecord || recordVisit.isPending}
                onClick={() => recordVisit.mutate()}
              >
                {recordVisit.isPending ? 'Recording…' : 'Record it'}
              </button>
            </div>
            {recordError ? (
              <p className="tc-warn" role="alert">
                {recordError}
              </p>
            ) : null}
          </div>
        ) : null}

        {delivered.isError && !delivered.data ? (
          <LoadFailure
            error={delivered.error}
            message="We couldn’t load the delivered visits just now. Do not read this as a day with no visits."
            onRetry={() => void delivered.refetch()}
          />
        ) : deliveries.length === 0 ? (
          <SuccessEmpty
            heading="No visits recorded in this window"
            message="A delivered visit appears here once somebody records one."
          />
        ) : (
          <ul className="tc-rows">
            {/* Delivered rows name their installation too, so the same honest
                note applies to this list: a degraded connector's records may be
                behind, and saying so is AGENTS.md §5. */}
            <DegradedSourceNote
              connectorNames={degradedConnectorNames(deliveries, connectorLookup)}
            />
            {deliveries.map((row) => {
              const type = typeLabel(row.serviceTypeId);
              const attendance =
                ATTENDANCE_LABELS[row.attendanceStatus as SVC.AttendanceStatus] ??
                row.attendanceStatus;
              /**
               * A delivered visit (`ServiceEvent`) now carries `integrationId`
               * and `lastSyncedAt` as well as `source` (PH-UX-002), so it names
               * the system it came from and when that system last confirmed it.
               *
               * It still carries NO `vendorAbsentSince`, and that is a fact
               * about the data rather than a gap here: only a PLANNED visit can
               * stop being listed by the vendor. A delivered row therefore
               * never renders the vendor-absent sentence.
               */
              const provenance = serviceEventProvenance(row, connectorLookup);
              const deliveredLastSynced =
                provenance.kind === 'imported' && provenance.lastSyncedAt
                  ? `${formatDay(provenance.lastSyncedAt)}, ${formatClock(provenance.lastSyncedAt)}`
                  : null;
              return (
                <li key={row.id} className="tc-row">
                  <div style={{ flex: 1 }}>
                    <span className="cmp-control-title">{clientLabel(row.clientId)}</span>
                    <p className="rn-muted">
                      {formatDay(row.occurredAt)} · {formatClock(row.occurredAt)}
                      {' · '}
                      {row.workerId === null ? 'Nobody attributed' : nameOf(row.workerId)}
                      {type ? ` · ${type}` : ''}
                      {row.minutes === null ? '' : ` · ${row.minutes} min`}
                      {provenance.kind === 'imported' ? (
                        <>
                          {' · '}
                          <Origin
                            source={row.source}
                            connectorName={provenance.connectorName}
                            lastSyncedAt={deliveredLastSynced}
                          />
                        </>
                      ) : null}
                    </p>
                  </div>
                  <Pill variant={row.attendanceStatus === 'attended' ? 'good' : 'unsure'} dot>
                    {attendance}
                  </Pill>
                  {row.scheduledServiceId === null ? (
                    <span className="rn-muted">Unplanned</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
